'use client';

import './email-code-step.css';

import { useEffect, useId, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { AnimatePresence, motion, useAnimate, useReducedMotion } from 'motion/react';
import { RiMailSendLine, RiRefreshLine } from '@remixicon/react';
import { useTranslations } from 'next-intl';
import { AuthErrorCode, EMAIL_CODE, type EmailSignInResponse } from '@mentorivo/shared';

import { FormAlert, SubmitButton } from '@/components/auth-parts';
import { ApiError } from '@/lib/api';
import { dur, ease, spring, springFade } from '@/lib/motion';
import { verifyEmailCode } from '@/lib/session';

/**
 * "Postana bak" adımı — giriş ve kayıt formunun ortak ikinci yarısı
 * (10 Eki 2026, parolanın yerine).
 *
 * Adrese tek bir posta gitti: içinde altı haneli kod VE bir giriş bağlantısı.
 * Bu ekran kodu bekliyor; bağlantıya tıklayan kişi başka bir sekmede
 * (`/auth/link`) zaten içeri giriyor, bu sekme ona engel olmuyor.
 *
 * ── Kod alanı: TEK girdi, altı görsel yuva ──────────────────────────────────
 * Altı ayrı `<input>` her tuşta odak taşımak, yapıştırmayı altıya bölmek ve
 * iOS'un "postadan kodu doldur" önerisini yalnız ilk kutuya basmak demekti.
 * Burada gerçek girdi TEK (`autocomplete="one-time-code"`, sayı tuş takımı) ve
 * yuvaların ÜSTÜNDE şeffaf duruyor; yuvalar yalnız gösterim. Yapıştırma,
 * otomatik doldurma, ekran okuyucu tek bir alanla konuşuyor.
 *
 * Altıncı hane girildiği an form kendiliğinden gönderiliyor — kişinin
 * "Devam" düğmesine uzanmasına gerek yok. Düğme yine duruyor: otomatik
 * gönderim başarısız olduysa (ağ) aynı kodu tekrar denemenin yolu o.
 *
 * ── Hareket neyi açıklıyor ─────────────────────────────────────────────────
 *   • Hane yuvasına DÜŞÜYOR (4px + opaklık, `snap` yayı): "yazdığın yere oturdu".
 *   • Yanlış kodda alan kısa bir yatay sarsıntı yapıyor ve haneler boşalıyor:
 *     "reddedildi, baştan" — metin satırı nedenini ve kalan hakkı söylüyor
 *     (hareket tek başına bilgi taşımıyor, DESIGN.md §1).
 *   • Etkin yuvanın imleci yanıp sönüyor — CSS, `prefers-reduced-motion`da
 *     sabit.
 * Sarsıntı `useAnimate` ile oynatılıyor ve `MotionConfig`in kapsamına
 * girmiyor — azaltılmış hareket tercihi `useReducedMotion` ile elle okunuyor.
 */

/**
 * Hata gövdesinin `details` alanı. `ApiError.details` gövdenin TAMAMI
 * (`{ code, message, details }`) — kalan hak ve bekleme süresi bir kat
 * içeride.
 */
function errorDetails(err: ApiError): { attemptsLeft?: number; retryAfterSeconds?: number } {
  const body = err.details as { details?: { attemptsLeft?: unknown; retryAfterSeconds?: unknown } } | null;
  const inner = body?.details;
  return {
    attemptsLeft: typeof inner?.attemptsLeft === 'number' ? inner.attemptsLeft : undefined,
    retryAfterSeconds:
      typeof inner?.retryAfterSeconds === 'number' ? inner.retryAfterSeconds : undefined,
  };
}

export interface EmailCodeResend {
  /// Yeni kod istendi — sayaç bu değerden başlar.
  resendInSeconds: number;
}

export function EmailCodeStep({
  email,
  initialResendSeconds,
  onResend,
  onBack,
  onSignedIn,
}: {
  email: string;
  initialResendSeconds: number;
  /// Formun kendi başlatma çağrısı (kayıt formu adı da gönderiyor). Fırlatırsa
  /// ekran hatayı basar; sayaç değişmez.
  onResend: () => Promise<EmailCodeResend>;
  onBack: () => void;
  onSignedIn: (session: EmailSignInResponse) => void;
}) {
  const t = useTranslations('emailCode');
  const uid = useId();
  const reduceMotion = useReducedMotion();

  const [code, setCode] = useState('');
  const [focused, setFocused] = useState(false);
  const [pending, setPending] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [notice, setNotice] = useState<string | undefined>();
  const [countdown, setCountdown] = useState(initialResendSeconds);

  const inputRef = useRef<HTMLInputElement>(null);
  const [scope, animate] = useAnimate();
  /* Aynı kodun iki kez gitmesini önler: altıncı hane otomatik gönderiyor,
     kişi aynı anda Enter'a da basabilir. */
  const inFlight = useRef(false);

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setInterval(() => setCountdown((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  async function submit(value: string) {
    if (inFlight.current || value.length !== EMAIL_CODE.length) return;
    inFlight.current = true;
    setPending(true);
    setError(undefined);
    setNotice(undefined);

    try {
      const session = await verifyEmailCode(email, value);
      // Bekleme açık kalıyor: yönlendirme bitene kadar alan kilitli.
      onSignedIn(session);
    } catch (err) {
      inFlight.current = false;
      setPending(false);
      setCode('');
      setError(describe(err));
      if (!reduceMotion) {
        void animate(
          scope.current,
          { x: [0, -7, 6, -4, 2, 0] },
          { duration: 0.36, ease: 'easeOut' },
        );
      }
      inputRef.current?.focus();
    }
  }

  function describe(err: unknown): string {
    if (!(err instanceof ApiError)) return t('notConnected');
    if (err.status === 429) return t('tooMany');
    if (err.code === AuthErrorCode.CODE_INVALID) {
      const left = errorDetails(err).attemptsLeft ?? 0;
      return left > 0 ? t('invalid', { left }) : t('exhausted');
    }
    if (err.code === AuthErrorCode.CODE_EXPIRED) return t('expired');
    return err.status >= 500 || err.status === 0 ? t('notConnected') : t('expired');
  }

  function onChange(raw: string) {
    // Kopyalanan kod "482 913" ya da "482-913" gelebiliyor.
    const next = raw.replace(/\D/g, '').slice(0, EMAIL_CODE.length);
    setCode(next);
    if (error) setError(undefined);
    if (next.length === EMAIL_CODE.length) void submit(next);
  }

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (code.length !== EMAIL_CODE.length) {
      setError(t('incomplete'));
      inputRef.current?.focus();
      return;
    }
    void submit(code);
  }

  async function resend() {
    if (countdown > 0 || resending) return;
    setResending(true);
    setError(undefined);
    setNotice(undefined);
    try {
      const result = await onResend();
      setCountdown(result.resendInSeconds);
      setCode('');
      setNotice(t('resent'));
      inputRef.current?.focus();
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        const wait = errorDetails(err).retryAfterSeconds;
        if (wait) setCountdown(wait);
        setError(t('tooMany'));
      } else {
        setError(err instanceof Error && err.message === 'not-sent' ? t('notSent') : t('notConnected'));
      }
    } finally {
      setResending(false);
    }
  }

  const inputId = `${uid}-code`;
  const leadId = `${uid}-lead`;
  const hintId = `${uid}-hint`;
  const active = Math.min(code.length, EMAIL_CODE.length - 1);

  return (
    <>
      {/* İkon kutu içinde değil, doğrudan zemine oturuyor (DESIGN.md §3).
          Yay `default`: bu bir kutlama değil, bir teyit — iş yarıda. */}
      <motion.span
        className="auth-sent-icon"
        initial={{ scale: 0.7, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={springFade(spring.default, dur.fast)}
        aria-hidden="true"
      >
        <RiMailSendLine size={26} />
      </motion.span>

      <div className="auth-head">
        <h1 className="auth-title">{t('title')}</h1>
        <p className="auth-lead" id={leadId}>
          {t.rich('lead', {
            address: email,
            email: (chunks) => <strong className="auth-sent-address">{chunks}</strong>,
            minutes: Math.round(EMAIL_CODE.ttlSeconds / 60),
          })}
        </p>
      </div>

      <form className="auth-form" onSubmit={onSubmit} noValidate>
        <div className="auth-field">
          <label className="auth-label" htmlFor={inputId}>
            {t('label')}
          </label>
          <div
            ref={scope}
            className="auth-code"
            data-focused={focused || undefined}
            data-invalid={error ? true : undefined}
          >
            <input
              ref={inputRef}
              id={inputId}
              className="auth-code-input"
              type="text"
              name="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]*"
              maxLength={EMAIL_CODE.length + 2}
              /* Odak doğrudan kodda: kişi postadan dönünce yazmaya (ya da
                 telefonda önerilen kodu seçmeye) başlayabilsin. Ekran okuyucu
                 kodun nereye gittiğini `aria-describedby` ile duyuyor. */
              autoFocus
              spellCheck={false}
              value={code}
              disabled={pending}
              onChange={(e) => onChange(e.target.value)}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              aria-invalid={Boolean(error)}
              aria-describedby={`${leadId} ${hintId}`}
            />
            <div className="auth-code-slots" aria-hidden="true">
              {Array.from({ length: EMAIL_CODE.length }, (_, index) => {
                const digit = code[index];
                return (
                  <span
                    key={index}
                    className="auth-code-slot"
                    data-filled={digit !== undefined || undefined}
                    data-active={(focused && index === active && !pending) || undefined}
                  >
                    <AnimatePresence initial={false}>
                      {digit !== undefined && (
                        <motion.span
                          key={`${index}-${digit}`}
                          className="auth-code-digit"
                          initial={{ opacity: 0, y: 4 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, transition: { duration: dur.instant } }}
                          transition={springFade(spring.snap, dur.instant)}
                        >
                          {digit}
                        </motion.span>
                      )}
                    </AnimatePresence>
                    {digit === undefined && <span className="auth-code-caret" />}
                  </span>
                );
              })}
            </div>
          </div>
          <p className="auth-code-hint" id={hintId}>
            {t('hint')}
          </p>
        </div>

        <FormAlert message={error} />

        <AnimatePresence initial={false}>
          {notice && !error && (
            <motion.p
              key="notice"
              className="auth-code-notice"
              role="status"
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, transition: { duration: dur.instant } }}
              transition={{ duration: dur.fast, ease: ease.out }}
            >
              {notice}
            </motion.p>
          )}
        </AnimatePresence>

        <SubmitButton pending={pending} label={t('submit')} pendingLabel={t('submitting')} />
      </form>

      <div className="auth-sent-actions">
        {/* Sayaç bitmeden pasif — arka arkaya beş tıklama beş posta demek.
            Kalan süre METİNDE: pasiflik tek başına nedenini söylemiyor. */}
        <motion.button
          type="button"
          className="auth-ghost"
          onClick={resend}
          disabled={countdown > 0 || resending || pending}
          aria-busy={resending}
          whileHover={countdown > 0 ? undefined : { scale: 1.01, y: -1 }}
          whileTap={countdown > 0 ? undefined : { scale: 0.99 }}
          transition={spring.snap}
        >
          <RiRefreshLine size={16} aria-hidden="true" />
          {countdown > 0 ? t('resendIn', { seconds: countdown }) : t('resend')}
        </motion.button>

        <button type="button" className="auth-text-button" onClick={onBack} disabled={pending}>
          {t('change')}
        </button>
      </div>
    </>
  );
}
